import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Crea un singolo client Supabase da usare in tutta l'app
const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Funzioni di servizio per i contatti
export const contattiService = {
  getAll: async () => {
    const { data, error } = await supabase
      .from('contatti')
      .select('*')
      .order('azienda');
    
    if (error) throw error;
    return data;
  },
  
  add: async (contatto) => {
    const { data, error } = await supabase
      .from('contatti')
      .insert([
        { 
          nome: contatto.nome,
          cognome: contatto.cognome,
          azienda: contatto.azienda,
          telefono: contatto.telefono,
          indirizzo: contatto.indirizzo,
          email: contatto.email,
          is_cliente: contatto.isCliente,
          is_vettore: contatto.isVettore
        }
      ])
      .select();
    
    if (error) throw error;
    return data[0];
  },
  
  update: async (contatto) => {
    const { data, error } = await supabase
      .from('contatti')
      .update({ 
        nome: contatto.nome,
        cognome: contatto.cognome,
        azienda: contatto.azienda,
        telefono: contatto.telefono,
        indirizzo: contatto.indirizzo,
        email: contatto.email,
        is_cliente: contatto.isCliente,
        is_vettore: contatto.isVettore
      })
      .eq('id', contatto.id)
      .select();
    
    if (error) throw error;
    return data[0];
  },
  
  delete: async (id) => {
    const { error } = await supabase
      .from('contatti')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
    return true;
  }
};

// Funzioni di servizio per i magazzini
export const magazziniService = {
  getAll: async () => {
    const { data, error } = await supabase
      .from('magazzini')
      .select('*')
      .order('nome');
    
    if (error) throw error;
    return data;
  },
  
  add: async (magazzino) => {
    const { data, error } = await supabase
      .from('magazzini')
      .insert([
        { 
          nome: magazzino.nome
        }
      ])
      .select();
    
    if (error) throw error;
    return data[0];
  },
  
  update: async (magazzino) => {
    const { data, error } = await supabase
      .from('magazzini')
      .update({ 
        nome: magazzino.nome
      })
      .eq('id', magazzino.id)
      .select();
    
    if (error) throw error;
    return data[0];
  },
  
  delete: async (id) => {
    const { error } = await supabase
      .from('magazzini')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
    return true;
  }
};

// Funzioni di servizio per i viaggi
export const viaggiService = {
  getAll: async () => {
    const { data, error } = await supabase
      .from('viaggi')
      .select('*')
      .order('data', { ascending: false });
    
    if (error) throw error;
    return data;
  },
  
  getByDateRange: async (startDate, endDate) => {
    const { data, error } = await supabase
      .from('viaggi')
      .select('*')
      .gte('data', startDate)
      .lte('data', endDate)
      .order('data', { ascending: false });
    
    if (error) throw error;
    return data;
  },
  
  add: async (viaggio) => {
    const { data, error } = await supabase
      .from('viaggi')
      .insert([
        { 
          data: viaggio.data,
          cliente_id: viaggio.clienteId || null,  // Usa null se è una stringa vuota
          vettore_id: viaggio.vettoreId ? viaggio.vettoreId : null,  // Usa null se è una stringa vuota
          magazzino_carico_id: viaggio.magazzinoCarico ? viaggio.magazzinoCarico : null,  // Usa null se è una stringa vuota
          magazzino_scarico_id: viaggio.magazzinoScarico ? viaggio.magazzinoScarico : null,  // Usa null se è una stringa vuota
          prezzo: viaggio.prezzoCliente ? viaggio.prezzoCliente : 0,  // Prezzo è NOT NULL, quindi usiamo 0 come default
          prezzo_vettore: viaggio.prezzoVettore ? viaggio.prezzoVettore : 0,  // Prezzo vettore è NOT NULL, quindi usiamo 0 come default
          bancali: viaggio.bancali || 26,  // Bancali ha un default di 26
          note: viaggio.note || ''  // Le stringhe vuote sono ok per il campo note (TEXT)
        }
      ])
      .select();
    
    if (error) throw error;
    return data[0];
  },
  
  update: async (viaggio) => {
    const { data, error } = await supabase
      .from('viaggi')
      .update({ 
          data: viaggio.data,
          cliente_id: viaggio.clienteId || null,  // Usa null se è una stringa vuota
          vettore_id: viaggio.vettoreId ? viaggio.vettoreId : null,  // Usa null se è una stringa vuota
          magazzino_carico_id: viaggio.magazzinoCarico ? viaggio.magazzinoCarico : null,  // Usa null se è una stringa vuota
          magazzino_scarico_id: viaggio.magazzinoScarico ? viaggio.magazzinoScarico : null,  // Usa null se è una stringa vuota
          prezzo: viaggio.prezzoCliente ? viaggio.prezzoCliente : 0,  // Prezzo è NOT NULL, quindi usiamo 0 come default
          prezzo_vettore: viaggio.prezzoVettore ? viaggio.prezzoVettore : 0,  // Prezzo vettore è NOT NULL, quindi usiamo 0 come default
          bancali: viaggio.bancali || 26,  // Bancali ha un default di 26
          note: viaggio.note || ''  // Le stringhe vuote sono ok per il campo note (TEXT)
      })
      .eq('id', viaggio.id)
      .select();
    
    if (error) throw error;
    return data[0];
  },
  
  delete: async (id) => {
    const { error } = await supabase
      .from('viaggi')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
    return true;
  }
};

// Funzioni di autenticazione
export const authService = {
  signUp: async (email, password) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    });
    
    if (error) throw error;
    return data;
  },
  
  signIn: async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (error) throw error;
    return data;
  },
  
  signOut: async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  },
  
  getCurrentUser: async () => {
    const { data, error } = await supabase.auth.getUser();
    if (error) throw error;
    return data?.user;
  },
  
// Funzione resetPassword aggiornata da inserire nel authService in supabase.js
resetPassword: async (email) => {
  // Usa l'URL dell'applicazione dalla variabile d'ambiente o fallback su window.location.origin
  const appUrl = process.env.REACT_APP_URL || window.location.origin;
  
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${appUrl}`
  });
  
  if (error) throw error;
  return true;
},
  
  changePassword: async (currentPassword, newPassword) => {
    // Prima verifichiamo l'attuale password
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) throw new Error('Utente non autenticato');
    
    // Verifica la password corrente
    const { error: signInError } = await supabase.auth.signInWithPassword({
      email: user.email,
      password: currentPassword,
    });
    
    if (signInError) throw new Error('Password attuale non corretta');
    
    // Cambia la password
    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });
    
    if (error) throw error;
    return true;
  }
};

export default supabase;